import { Injectable } from '@angular/core';
import { Firestore, where, orderBy, QueryConstraint, collection, onSnapshot, query } from '@angular/fire/firestore';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AuthService } from '@auth';

import { extractSubdomain } from '../../helpers/subdomain-mask';

import { BaseService } from './../base/base.service';
import { Team } from './../../models/team.interface';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class TeamService extends BaseService<Team> {
  protected readonly documentPath = 'teams';
  private productiveCached$: Observable<Team[]>;

  private userAuthorizedTeams$ = new BehaviorSubject<Team[]>(undefined);
  public authorizedTeams$ = this.userAuthorizedTeams$.asObservable();
  public authorizedTeamsFromCache = false;

  constructor(
    protected db: Firestore,
    protected toastService: ToastService,
    private authService: AuthService
  ) {
    super(db, toastService);

    this.authService.user$
      .pipe(
        switchMap((user) => {
          if (!user) return of(undefined);
          const queryConstraints = this.constructQuery(user);
          return new Observable<Team[]>((subscriber) =>
            onSnapshot(
              query(collection(this.db, this.documentPath), ...queryConstraints),
              { includeMetadataChanges: true },
              (snapshot) => {
                this.authorizedTeamsFromCache = snapshot.metadata.fromCache;
                subscriber.next(snapshot.docs.map((document) => this.convertFirestoreTimeStamps(document.data() as Team)));
              },
              (error) => subscriber.error(error)
            )
          );
        })
      )
      .subscribe({
        next: (teams) => this.userAuthorizedTeams$.next(teams),
        error: () => {
          this.userAuthorizedTeams$.next(undefined);
          this.logService.danger('Het laden van geautoriseerde teams is mislukt.');
          this.toastService.showError('Het laden van je teams is mislukt. Vernieuw de pagina en probeer het opnieuw.');
        },
      });
  }

  private constructQuery(user): QueryConstraint[] {
    //const constraints: QueryConstraint[] = [orderBy('teamnaam', 'asc')];
    const constraints: QueryConstraint[] = [orderBy('teamnaam', 'asc'), where('productief', '==', true)];

    if (user.roles.admin || user.roles.wlzadmin) {
      // Beheerders hebben toegang tot alle teams, geen extra filters nodig.
    } else if (user.roles.coach && user.regiocoachId && user.regiocoachId > 0) {
      constraints.push(where('regiocoachId', '==', user.regiocoachId));
    } else if (user.roles.employee) {
      constraints.push(where('members', 'array-contains', user.email));
    }

    return constraints;
  }

  getAllProductiveCached(): Observable<Team[]> {
    if (!this.productiveCached$) {
      this.logService.warning('getAllProductiveCached');
      this.productiveCached$ = this.getAllProductive().pipe(
        tap(() => this.logService.info(`[getAllProductiveCached] path: ${this.documentPath}`)),
        shareReplay({ refCount: true, bufferSize: 1 })
      );
    } else {
      this.logService.success('getAllCached');
    }

    return this.productiveCached$;
  }

  getTeamFromSubdomain(subdomain: string) {
    return this.getAllCached().pipe(
      take(1),
      map((teams) => teams.filter((team) => extractSubdomain(team.teamnaam) == subdomain && team.productief == true && team.email != null))
    );
  }

  getAllProductive(): Observable<Team[]> {
    this.logService.warning('getAllProductive');
    return this.getWithQuery([where('productief', '==', true), where('eindDatum', '>', new Date()), orderBy('teamnaam', 'asc')]);
  }
}
